import { useEffect, useMemo, useReducer, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useLocation, useRoute } from "wouter";
import { useTranslation } from "react-i18next";
import { Sakura } from "../components/shrine/Sakura";
import { Bell } from "../components/omikuji/Bell";
import { KujiBox } from "../components/omikuji/KujiBox";
import { StartOverlay } from "../components/ui/StartOverlay";
import { Miko } from "../components/miko/Miko";
import { useSound } from "../hooks/useSound";
import { useShake } from "../hooks/useShake";
import { useVibration, VibrationPatterns } from "../hooks/useVibration";
import { drawOmikuji, type DrawResult } from "../lib/omikuji";
import { ensureSeedPair, getPair } from "../lib/storage";

/**
 * §2.4 ペア儀式 (中規模版・約25秒)
 *  miko (3s) → bell (待機) → box+stick (3s) → drawing (4s) → waiting (3-5s, 相手が引くのを待つ)
 *  待機完了 → SimultaneousReveal へ遷移
 *
 * 結果は session ストレージに渡して同時開示画面で復元する。
 */

type Step = "miko" | "shake" | "stick" | "drawing" | "waiting";
const ORDER: Step[] = ["miko", "shake", "stick", "drawing", "waiting"];
const AUTO: Partial<Record<Step, number>> = { miko: 2500, stick: 2500, drawing: 4000 };

const SESSION_KEY = "musubi.pendingPairDraw";

type PendingDraw = {
  pairId: string;
  partnerName: string;
  yourResult: DrawResult;
  partnerResult: DrawResult;
  startedAt: string;
};

function reducer(s: { step: Step }, _a: { type: "advance" }): { step: Step } {
  const i = ORDER.indexOf(s.step);
  return { step: ORDER[Math.min(i + 1, ORDER.length - 1)] };
}

export function PairRitual() {
  const { t } = useTranslation();
  const [, params] = useRoute("/pair/:pairId/ritual");
  const [, navigate] = useLocation();
  const reduce = useReducedMotion();
  const { unlock, play } = useSound();
  const { vibrate } = useVibration();
  const shake = useShake({ goal: 6 });

  const pairId = params?.pairId ?? "seed_yu";
  const [partnerName, setPartnerName] = useState("ゆう");

  useEffect(() => {
    ensureSeedPair();
    const p = getPair(pairId);
    if (p) setPartnerName(p.partnerName);
  }, [pairId]);

  const yourResult = useMemo<DrawResult>(() => drawOmikuji({ partnerName }), [partnerName]);
  const partnerResult = useMemo<DrawResult>(() => drawOmikuji({ partnerName: "あなた" }), []);

  const [{ step }, dispatch] = useReducer(reducer, { step: "miko" });
  const [started, setStarted] = useState(false);
  const startedRef = useRef(false);
  const [waitDots, setWaitDots] = useState(0);

  // shake 完了
  useEffect(() => {
    if (step === "shake" && shake.completed) {
      play("kirari");
      vibrate(VibrationPatterns.bellShake);
      const id = window.setTimeout(() => dispatch({ type: "advance" }), 400);
      return () => clearTimeout(id);
    }
  }, [step, shake.completed, play, vibrate]);

  // 自動進行
  useEffect(() => {
    const dur = reduce ? 600 : AUTO[step];
    if (!dur) return;
    const id = window.setTimeout(() => dispatch({ type: "advance" }), dur);
    return () => clearTimeout(id);
  }, [step, reduce]);

  // 効果音
  useEffect(() => {
    switch (step) {
      case "stick":
        play("woodTap");
        vibrate(VibrationPatterns.woodTap);
        break;
      case "waiting":
        // pending を sessionStorage に格納 → SimultaneousReveal へ
        try {
          const pending: PendingDraw = {
            pairId,
            partnerName,
            yourResult,
            partnerResult,
            startedAt: new Date().toISOString(),
          };
          sessionStorage.setItem(SESSION_KEY, JSON.stringify(pending));
        } catch {
          /* noop */
        }
        break;
    }
  }, [step, play, vibrate, pairId, partnerName, yourResult, partnerResult]);

  // waiting: 相手が引く演出 (3.5s 後に reveal へ)
  useEffect(() => {
    if (step !== "waiting") return;
    const dotsId = window.setInterval(() => setWaitDots((n) => (n + 1) % 4), 500);
    const goId = window.setTimeout(() => navigate(`/pair/${pairId}/reveal`), reduce ? 800 : 3500);
    return () => {
      clearInterval(dotsId);
      clearTimeout(goId);
    };
  }, [step, navigate, pairId, reduce]);

  function startSequence() {
    if (startedRef.current) return;
    startedRef.current = true;
    setStarted(true);
    unlock();
    void shake.requestPermission();
  }

  return (
    <main className="relative min-h-dvh overflow-hidden bg-[var(--color-night-deep)] text-[var(--color-washi)] select-none">
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at center 80%, rgba(159,29,53,0.18), transparent 60%), linear-gradient(to bottom, #050203 0%, #0a0608 50%, #1a0c12 100%)",
        }}
      />
      <Sakura count={10} />

      <div className="relative z-10 min-h-dvh flex flex-col items-center justify-center px-4 py-8">
        <AnimatePresence>
          {step === "miko" && (
            <motion.div
              key="miko"
              initial={{ opacity: 0, y: 60 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.9 }}
            >
              <Miko size={200} />
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {step === "shake" && (
            <motion.div
              key="bell"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="flex flex-col items-center gap-4"
            >
              <Bell
                intensity={shake.intensity}
                onTap={() => {
                  shake.tap();
                  play("bellTap");
                  vibrate(VibrationPatterns.bellTap);
                }}
                size={200}
              />
              <div className="w-32 h-1 rounded-full bg-white/10 overflow-hidden">
                <motion.div className="h-full bg-[var(--color-gold)]" animate={{ width: `${shake.intensity * 100}%` }} />
              </div>
              <p
                className="text-sm tracking-[0.2em] text-[var(--color-gold-light)]"
                style={{ fontFamily: "var(--font-fude)" }}
              >
                {t("ritual.shakeBell")}
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {step === "stick" && (
            <motion.div key="box" initial={{ opacity: 0, y: 60 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
              <KujiBox stickOut size={150} />
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {step === "drawing" && (
            <motion.div
              key="drawing"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex flex-col items-center gap-4"
            >
              <Miko size={180} />
              <p
                className="text-sm tracking-widest text-[var(--color-gold-light)]"
                style={{ fontFamily: "var(--font-fude)" }}
              >
                {t("ritual.drawing")}
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {step === "waiting" && (
            <motion.div
              key="waiting"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex flex-col items-center gap-6"
            >
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
                className="text-5xl text-[var(--color-shu-light)]"
                style={{ filter: "drop-shadow(0 0 16px rgba(159,29,53,0.6))" }}
              >
                ⛩
              </motion.div>
              <p
                className="text-sm tracking-widest text-[var(--color-gold-light)] text-center"
                style={{ fontFamily: "var(--font-fude)" }}
              >
                {t("pair.waitingForPartner", { name: partnerName })}
                {".".repeat(waitDots)}
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <AnimatePresence>
        {!started && (
          <StartOverlay title={t("pair.draw")} hint="TAP TO BEGIN" onStart={startSequence} />
        )}
      </AnimatePresence>
    </main>
  );
}

export const PAIR_DRAW_SESSION_KEY = SESSION_KEY;
